/**
 * Neon (ART-BIBLE §6). No postprocessing anywhere in this game — glow is geometry:
 *   core plane (sharp glyph mask) + 2 blurred halo planes + a light pool on the floor.
 * Real neon flickers in steps, never smoothly: FLICKER = 70 ms quantised noise.
 */

import {
  Group, Mesh, PlaneGeometry, MeshBasicMaterial, AdditiveBlending, CanvasTexture,
  NoColorSpace, LinearFilter, Color, DoubleSide, TubeGeometry, CatmullRomCurve3,
  Vector3, type Texture, type Object3D,
} from 'three';
import { NEON, type NeonName, mixHex } from './palette';
import { FONT_NEON, rng } from './textures';

const PLANE = new PlaneGeometry(1, 1);

interface GlyphCanvas { tex: Texture; w: number; h: number; }

function maskCanvas(draw: (g: CanvasRenderingContext2D, w: number, h: number) => void, w: number, h: number, blur = 0): GlyphCanvas {
  const c = document.createElement('canvas');
  c.width = Math.max(4, Math.ceil(w)); c.height = Math.max(4, Math.ceil(h));
  const g = c.getContext('2d')!;
  if (blur > 0) g.filter = `blur(${blur}px)`;
  draw(g, c.width, c.height);
  const t = new CanvasTexture(c);
  t.colorSpace = NoColorSpace;
  t.minFilter = LinearFilter; t.magFilter = LinearFilter;
  t.generateMipmaps = false;
  return { tex: t, w: c.width, h: c.height };
}

export interface NeonOpts {
  color?: NeonName;
  /** world-unit cap height */
  height?: number;
  letterSpacing?: string;
  /** flicker personality: 0 = rock steady, 1 = about to die */
  sickness?: number;
  seed?: number;
}

export interface NeonPiece {
  group: Group;
  core: Mesh;
  glows: Mesh[];
  /** 0..1 — set by the flicker driver or by hand for the sign gag */
  intensity: number;
  sickness: number;
  seed: number;
  width: number;
  height: number;
  /** when true the flicker driver leaves this piece alone (scripted gags) */
  userLocked?: boolean;
  setIntensity(v: number): void;
}

const _pieces: NeonPiece[] = [];

function buildPiece(
  drawGlyph: (g: CanvasRenderingContext2D, w: number, h: number) => void,
  pxW: number, pxH: number, worldH: number, color: NeonName, sickness: number, seed: number,
): NeonPiece {
  const core = maskCanvas(drawGlyph, pxW, pxH, 0);
  const g1 = maskCanvas(drawGlyph, pxW, pxH, Math.max(2, pxH * 0.035));
  const g2 = maskCanvas(drawGlyph, pxW, pxH, Math.max(5, pxH * 0.10));

  const aspect = pxW / pxH;
  const h = worldH, w = worldH * aspect;

  const cCol = new Color(mixHex(NEON[color].core, 0xffffff, 0.62));
  const coreMat = new MeshBasicMaterial({
    map: core.tex, alphaMap: core.tex, color: cCol, transparent: true,
    blending: AdditiveBlending, depthWrite: false, side: DoubleSide, toneMapped: false,
  });
  const coreMesh = new Mesh(PLANE, coreMat);
  coreMesh.scale.set(w, h, 1);
  coreMesh.userData.noInk = true;

  const mk = (gc: GlyphCanvas, hex: number, op: number, scale: number) => {
    const m = new MeshBasicMaterial({
      map: gc.tex, alphaMap: gc.tex, color: new Color(hex), transparent: true,
      blending: AdditiveBlending, depthWrite: false, opacity: op, side: DoubleSide, toneMapped: false,
    });
    const mesh = new Mesh(PLANE, m);
    mesh.scale.set(w * scale, h * scale, 1);
    mesh.position.z = -0.01;
    mesh.userData.noInk = true;
    return mesh;
  };
  const glow1 = mk(g1, NEON[color].core, 0.85, 1.02);
  const glow2 = mk(g2, NEON[color].halo, 0.5, 1.06);

  const group = new Group();
  group.add(glow2, glow1, coreMesh);
  group.userData.noInk = true;

  const piece: NeonPiece = {
    group, core: coreMesh, glows: [glow1, glow2],
    intensity: 1, sickness, seed, width: w, height: h,
    setIntensity(v: number) {
      this.intensity = v;
      coreMat.opacity = Math.min(1, v * 1.0);
      (glow1.material as MeshBasicMaterial).opacity = 0.85 * v;
      (glow2.material as MeshBasicMaterial).opacity = 0.5 * v;
      coreMesh.visible = v > 0.02;
      glow1.visible = v > 0.02;
      glow2.visible = v > 0.02;
    },
  };
  _pieces.push(piece);
  return piece;
}

/** a neon word; `hole` blanks one character slot so a swap-letter can live there */
export function neonWord(text: string, opts: NeonOpts & { hole?: number } = {}): NeonPiece & { holeX: number; holeW: number } {
  const color = opts.color ?? 'magenta';
  const px = 128;
  const font = `${px}px ${FONT_NEON}`;
  const probe = document.createElement('canvas').getContext('2d')!;
  probe.font = font;
  if (opts.letterSpacing) (probe as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
  const full = probe.measureText(text).width;
  const pad = px * 0.55;
  const pxW = full + pad * 2, pxH = px * 1.75;

  const hole = opts.hole ?? -1;
  const pre = hole >= 0 ? text.slice(0, hole) : text;
  const post = hole >= 0 ? text.slice(hole + 1) : '';
  const preW = probe.measureText(pre).width;
  const holeChar = hole >= 0 ? text[hole] : '';
  const holeW = hole >= 0 ? probe.measureText(holeChar).width : 0;

  const draw = (g: CanvasRenderingContext2D, w: number, h: number) => {
    g.font = font;
    if (opts.letterSpacing) (g as unknown as { letterSpacing: string }).letterSpacing = opts.letterSpacing;
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    g.fillStyle = '#ffffff';
    g.fillText(pre, pad, h / 2);
    if (hole >= 0) g.fillText(post, pad + preW + holeW, h / 2);
  };

  const piece = buildPiece(draw, pxW, pxH, opts.height ?? 3, color, opts.sickness ?? 0.06, opts.seed ?? 1);
  const scale = piece.width / pxW;
  return Object.assign(piece, {
    holeX: (pad + preW + holeW / 2 - pxW / 2) * scale,
    holeW: holeW * scale,
  });
}

/** a single neon character, for the sign gag's swap slot */
export function neonGlyph(ch: string, opts: NeonOpts = {}): NeonPiece {
  const px = 128;
  const font = `${px}px ${FONT_NEON}`;
  const probe = document.createElement('canvas').getContext('2d')!;
  probe.font = font;
  const w = probe.measureText(ch).width;
  const pad = px * 0.55;
  const pxW = w + pad * 2, pxH = px * 1.75;
  const draw = (g: CanvasRenderingContext2D, cw: number, ch2: number) => {
    g.font = font;
    g.textBaseline = 'middle';
    g.textAlign = 'center';
    g.fillStyle = '#ffffff';
    g.fillText(ch, cw / 2, ch2 / 2);
  };
  return buildPiece(draw, pxW, pxH, opts.height ?? 3, opts.color ?? 'magenta', opts.sickness ?? 0.4, opts.seed ?? 7);
}

/** a bent glass tube — cabinet trim lighting. 2 draws. */
export function neonTube(points: Vector3[], opts: NeonOpts & { radius?: number } = {}): NeonPiece {
  const color = opts.color ?? 'cyan';
  const r = opts.radius ?? 0.12;
  const curve = new CatmullRomCurve3(points, false, 'catmullrom', 0.2);
  const coreGeo = new TubeGeometry(curve, Math.max(8, points.length * 6), r, 6, false);
  const glowGeo = new TubeGeometry(curve, Math.max(8, points.length * 6), r * 3.2, 6, false);

  const coreMat = new MeshBasicMaterial({
    color: new Color(mixHex(NEON[color].core, 0xffffff, 0.55)), toneMapped: false,
  });
  const glowMat = new MeshBasicMaterial({
    color: new Color(NEON[color].core), transparent: true, opacity: 0.28,
    blending: AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  const core = new Mesh(coreGeo, coreMat);
  const glow = new Mesh(glowGeo, glowMat);
  core.userData.noInk = true; glow.userData.noInk = true;
  const group = new Group();
  group.add(glow, core);
  group.userData.noInk = true;

  const piece: NeonPiece = {
    group, core, glows: [glow], intensity: 1,
    sickness: opts.sickness ?? 0.03, seed: opts.seed ?? 3, width: 0, height: 0,
    setIntensity(v: number) {
      this.intensity = v;
      coreMat.color.set(mixHex(0x000000, mixHex(NEON[color].core, 0xffffff, 0.55), v));
      glowMat.opacity = 0.28 * v;
    },
  };
  _pieces.push(piece);
  return piece;
}

/** the light a sign throws on the floor — this is what sells "lit by machines" */
export function floorPool(color: NeonName, radius: number, texture: Texture, opacity = 0.5): Mesh {
  const m = new MeshBasicMaterial({
    map: texture, color: new Color(NEON[color].core), transparent: true,
    blending: AdditiveBlending, depthWrite: false, opacity, toneMapped: false,
  });
  const mesh = new Mesh(PLANE, m);
  mesh.scale.set(radius * 2, radius * 2, 1);
  mesh.rotation.x = -Math.PI / 2;
  mesh.userData.noInk = true;
  mesh.renderOrder = 2;
  return mesh;
}

// ---------------------------------------------------------------- flicker

const _rand = rng(0xbeef);
const _phase = new Map<NeonPiece, { until: number; v: number }>();

/**
 * FLICKER = 70 ms quantum. Healthy tubes sit at 1.0 with a rare 1-frame dropout;
 * sick tubes stutter. Never a smooth sine — that reads as "pulsing UI", not as neon.
 */
export function updateNeon(tNow: number): void {
  for (const p of _pieces) {
    if (p.sickness <= 0) continue;
    let ph = _phase.get(p);
    if (!ph || tNow >= ph.until) {
      const r = _rand();
      const sick = p.sickness;
      let v = 1;
      if (r < sick * 0.35) v = 0.12 + _rand() * 0.2;
      else if (r < sick * 0.75) v = 0.55 + _rand() * 0.3;
      else v = 0.94 + _rand() * 0.06;
      ph = { until: tNow + 0.07 * (1 + Math.floor(_rand() * 5)), v };
      _phase.set(p, ph);
    }
    if (p.userLocked !== true) p.setIntensity(ph.v);
  }
}

export function neonPieces(): NeonPiece[] { return _pieces; }

export function disposeNeon(_root?: Object3D): void {
  _pieces.length = 0;
  _phase.clear();
}
